import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { MODULES } from '../config/modules.config';
import type { ModuleConfig } from '../config/modules.config';

import { isTenantAudienceSurface } from './supportWidgetSurfaces';

// Declare the global config type
declare global {
  interface Window {
    PanLumaWidgetConfig?: {
      reporter?: {
        name?: string;
        email?: string;
      };
      product?: string;
      /** Set to false to show name/email fields even when pre-filled. Default: hide when both are provided. */
      hideReporterFields?: boolean;
      /** Bearer token forwarded to the widget submit endpoint so the backend can attribute the ticket to the signed-in user. */
      authToken?: string;
      /**
       * Explicit widget language hint. The embeddable widget resolves this first
       * (before <html lang>/navigator.language), so dogfooding our own SPA always
       * shows the widget in the user's active app language — reliable regardless of
       * the widget's auto-detect timing. The widget normalizes e.g. "he-IL" -> "he"
       * and ignores unsupported languages (falling back to its configured default).
       */
      language?: string;
    };
  }
}

/**
 * Resolve which module a page path belongs to, using the canonical module
 * registry (`basePaths` from `modules_entities.config.json`) so URL→module
 * resolution is single-sourced and covers multi-path modules (email, files,
 * shipments) automatically. Returns `undefined` when no non-home module matches
 * (e.g. the home dashboard `/`).
 */
function getWidgetModuleForPath(pathname: string): ModuleConfig | undefined {
  for (const module of MODULES) {
    // `home` has basePath `/`, which prefix-matches everything — skip it so it
    // never shadows a real module (and `/` itself resolves to no module).
    if (module.id === 'home') continue;
    for (const basePath of module.basePaths) {
      // Match on a full segment boundary so `/sales` doesn't match `/salesfoo`.
      if (pathname === basePath || pathname.startsWith(`${basePath}/`)) {
        return module;
      }
    }
  }
  return undefined;
}

/**
 * Frontend-registry module ids that resolve from a URL prefix but are NOT
 * customer-facing products, so no ticket product should be set on their pages.
 * The dogfood support tenant configures a master-data product only for real
 * product modules; sending an internal-area slug would just fail to resolve
 * (harmless), but we exclude them so the widget stays cleanly unset there —
 * preserving the prior "no product on non-product areas" behaviour. `home` is
 * already skipped inside {@link getWidgetModuleForPath}.
 */
const NON_PRODUCT_MODULE_IDS = new Set<string>([
  'sysadmin',
  'developer',
  'coding',
  'design-system',
]);

/**
 * The dogfood tenant's product for master data's contacts & organizations area,
 * which Software Factory tracks as the module **Master Data - Orgs/contacts**.
 */
const MASTER_DATA_ORGS_PRODUCT = 'master-data-contacts-orgs';
/**
 * The dogfood tenant's product for master data's catalog area, which Software
 * Factory tracks as the module **Master Data - Products**.
 */
const MASTER_DATA_CATALOG_PRODUCT = 'master-data-contacts-products';

/**
 * `master_data` is ONE frontend module (`/master-data`) but TWO dogfood
 * products, one per sub-module — so its pages cannot use the module-slug rule
 * every other module uses. This maps the segment after `/master-data/` to the
 * product that owns it.
 *
 * Keyed on that segment rather than on a path prefix so a detail route
 * (`/master-data/contacts/:id`) and its list route classify together and a new
 * sub-route of an existing area needs no entry. A segment deliberately ABSENT
 * here yields no product, which is the right answer for a page that serves both
 * areas: `settings` renders contacts *and* catalog preferences, so guessing one
 * would file half of those reports against the wrong sub-module. The widget's
 * own product dropdown is the fallback there.
 *
 * Pinned exhaustively against `App.tsx`'s registered `/master-data/*` routes by
 * `__tests__/SupportWidgetConfig.test.ts`: a new master-data area fails that test
 * until it is classified here, rather than silently sending no product.
 */
const MASTER_DATA_PRODUCT_BY_SEGMENT: Record<string, string> = {
  // Contacts & organizations (nav section `nav.masterData.contactsAndOrgs`).
  organizations: MASTER_DATA_ORGS_PRODUCT,
  /** Legacy mount that redirects to `/master-data/organizations`. */
  companies: MASTER_DATA_ORGS_PRODUCT,
  contacts: MASTER_DATA_ORGS_PRODUCT,
  /** Customer tiers — a contact/organization classification, not a catalog one. */
  tiers: MASTER_DATA_ORGS_PRODUCT,

  // Catalog (nav sections `nav.masterData.catalog` and `nav.masterData.setup`).
  products: MASTER_DATA_CATALOG_PRODUCT,
  bundles: MASTER_DATA_CATALOG_PRODUCT,
  /** Product categories. */
  categories: MASTER_DATA_CATALOG_PRODUCT,
  'tax-categories': MASTER_DATA_CATALOG_PRODUCT,
  'price-lists': MASTER_DATA_CATALOG_PRODUCT,
  'pricing-rules': MASTER_DATA_CATALOG_PRODUCT,
  'pricing-campaigns': MASTER_DATA_CATALOG_PRODUCT,
  /** A product's sellable offering (`ProductReference`/`offering_id`). */
  offerings: MASTER_DATA_CATALOG_PRODUCT,
};

/**
 * Resolve the current page path to the product **slug** the embedded widget
 * should send, or `undefined` when the page has no product module.
 *
 * The slug is the first segment of the URL path (e.g. `/pipeline/deals/42` →
 * `pipeline`). The dogfood support tenant (sysadmin.panluma.ai) has
 * self-configured its products so each product's **SKU equals this slug**, and
 * the backend widget path resolves the submitted `product` string by name-or-SKU
 * (`WidgetService._resolve_product_id`). Sending the URL slug therefore routes
 * the ticket to the right product without a hand-maintained name table — and,
 * downstream, to the right Software Factory module when the ticket is converted
 * to a work item (`ModuleService.find_module_id_by_product`).
 *
 * `master_data` is the one module that splits: see
 * {@link MASTER_DATA_PRODUCT_BY_SEGMENT}.
 *
 * Gated by {@link getWidgetModuleForPath} so only a recognised product module's
 * pages set a product; internal areas (see {@link NON_PRODUCT_MODULE_IDS}), the
 * home dashboard, and unknown paths return `undefined`.
 */
export function getWidgetProductForPath(pathname: string): string | undefined {
  const module = getWidgetModuleForPath(pathname);
  if (!module || NON_PRODUCT_MODULE_IDS.has(module.id)) return undefined;
  const segments = pathname.split('/').filter(Boolean);
  if (module.id === 'master_data') {
    return MASTER_DATA_PRODUCT_BY_SEGMENT[(segments[1] ?? '').toLowerCase()];
  }
  // First non-empty path segment = the module slug the SKU is keyed on. The
  // matched module's basePath is always a segment-boundary prefix of the path,
  // so this segment is that module's slug (e.g. an /onedrive route on the files
  // module yields `onedrive`).
  const slug = segments[0];
  return slug ? slug.toLowerCase() : undefined;
}

/** Dogfood widget key of PanLuma's own local support tenant. */
const LOCAL_WIDGET_KEY = 'wgt_panlumasupport000000000000000000';
/** Dogfood widget key of PanLuma's production support tenant. */
const PRODUCTION_WIDGET_KEY = 'wgt_fbb3ff3e10a7d067168c583bf48e20ba';
/** The deployed app that serves the production widget script. */
const PRODUCTION_WIDGET_ORIGIN = 'https://app.panluma.ai';
/** Marks the tag we appended, so a re-render never appends a second one. */
const WIDGET_SCRIPT_ID = 'panluma-support-widget-script';

/**
 * Where the widget script is fetched from. Takes the location rather than
 * reading `window` so both branches are reachable from a test — jsdom's
 * hostname is always `localhost`, which would otherwise leave the production
 * key (the one that decides which support desk receives real tickets) unpinned.
 *
 * **The protocol is load-bearing, not decoration.** A bundled native shell
 * serves the app's own asset bundle from a LOCAL origin — `https://localhost`
 * on Capacitor Android (`androidScheme: 'https'`), `capacitor://localhost` on
 * iOS, `tauri://localhost` on the Tauri desktop app — so its hostname is
 * `localhost` while nothing whatsoever answers `/api` there. Keying only on the
 * hostname pointed the `<script src>` at the bundle, which 404s, and the widget
 * silently never mounted on any native app (mobile web was fine, since its
 * hostname is the real one). Requiring plain `http:` separates the two without
 * needing platform detection at all: the dev server is always local http, and
 * every bundled shell is on a non-http scheme (or, on Windows Tauri,
 * `http://tauri.localhost`, whose hostname already fails the test). This is the
 * same trap `resolveApiBaseUrl()` in `shared/api/config.ts` documents, and the
 * failure direction is safe — anything unrecognised gets the real, working
 * production widget rather than a dead script tag.
 */
export function supportWidgetScriptSrc(
  location: Pick<Location, 'hostname' | 'origin' | 'protocol'> = window.location,
): string {
  const { hostname, origin, protocol } = location;
  const isDevServer =
    protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1');
  // Local dev goes through the current origin so Vite's /api proxy forwards to
  // whatever backend this dev server targets (the worktree's, or :5005 on main).
  return isDevServer
    ? `${origin}/api/v1/public/support/widget/script.js?key=${LOCAL_WIDGET_KEY}`
    : `${PRODUCTION_WIDGET_ORIGIN}/api/v1/public/support/widget/script.js?key=${PRODUCTION_WIDGET_KEY}`;
}

/**
 * Loads the PanLuma Support Widget and configures it with the current user's
 * info and the current module context as a product name.
 *
 * **It owns the loading, not just the configuration.** The launcher used to be
 * injected by a `<script>` in `index.html`, which runs before React and knows no
 * route — so it mounted on every page including a tenant's white-labelled
 * customer portal, offering PanLuma's own support desk to somebody who is not
 * PanLuma's customer (and covering their content at 375px, since it is
 * `position: fixed` and no portal page reserves a bottom band for it).
 * Injecting from here is what lets the decision read the route:
 * {@link isTenantAudienceSurface}.
 *
 * Loading is one-shot per document. Crossing between the app and a tenant
 * portal is always a full page load — the two live in different auth contexts
 * and nothing in either tree links to the other — so the route at injection
 * time is the route for the session.
 *
 * Place this component in your app layout after authentication is established.
 */
export function SupportWidgetConfig() {
  const { user, accessToken } = useAuth();
  const location = useLocation();
  const { i18n } = useTranslation();

  const onTenantAudienceSurface = isTenantAudienceSurface(location.pathname);

  useEffect(() => {
    if (onTenantAudienceSurface) return;
    if (document.getElementById(WIDGET_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = WIDGET_SCRIPT_ID;
    script.src = supportWidgetScriptSrc();
    script.async = true;
    document.head.appendChild(script);
  }, [onTenantAudienceSurface]);

  useEffect(() => {
    // No widget here, so nothing may carry a PanLuma user's name, email or
    // bearer token onto a tenant's own surface either.
    if (onTenantAudienceSurface) {
      window.PanLumaWidgetConfig = undefined;
      return;
    }

    if (user) {
      window.PanLumaWidgetConfig = {
        reporter: {
          name: user.full_name || user.email,
          email: user.email,
        },
        product: getWidgetProductForPath(location.pathname),
        authToken: accessToken ?? undefined,
        // Render the widget in the user's active app language (dogfood reliability).
        language: i18n.language,
      };
    } else {
      window.PanLumaWidgetConfig = undefined;
    }

    return () => {
      window.PanLumaWidgetConfig = undefined;
    };
  }, [user, accessToken, location.pathname, i18n.language, onTenantAudienceSurface]);

  return null;
}
